// R1 — Corpus client. Fetches and zod-validates the static reading corpus that
// `scripts/build-corpus.ts` writes to `public/corpus/`: the catalog, per-chapter
// `DocumentModel`s (original + retelling), and the anchor sidecar. Everything is
// served over plain HTTP from the CDN — the anonymous reading path never touches
// Convex (SPEC "Reader UX").
//
// Filenames the catalog carries are content-hashed, so a response is immutable
// for the life of that name; a simple module-level promise cache both dedupes
// concurrent requests and survives for the session. Promises (not resolved
// values) are cached so React 19's `use()` can unwrap them under Suspense
// without a data-fetching effect (react-useeffect-discipline).

import type { z } from "zod";
import { env } from "@/env";
import { createClientLogger } from "@/logging";
import {
  corpusAnchorsSchema,
  type CorpusAnchors,
} from "@bindery/shared/corpus/anchors";
import {
  corpusCatalogSchema,
  type CatalogBook,
  type CatalogChapter,
  type CatalogMatter,
  type CorpusCatalog,
} from "@bindery/shared/corpus/catalog";
import {
  documentModelSchema,
  type DocumentModel,
} from "@bindery/shared/corpus/document";

/**
 * Public base path for the corpus artifacts. A leading-slash absolute path so
 * it resolves the same from any route depth; overridable at build time for a
 * CDN origin split (phase 7) via `VITE_CORPUS_BASE`.
 */
const CORPUS_BASE = env.VITE_CORPUS_BASE.replace(/\/$/, "");

const CATALOG_URL = `${CORPUS_BASE}/catalog.json`;

/** One promise per URL: content-hashed names make cached responses immutable. */
const cache = new Map<string, Promise<unknown>>();

/**
 * The URLs whose cached promise REJECTED — the only entries `retryCorpusFetch`
 * drops. Tracked separately because a rejected promise is indistinguishable
 * from a pending one without awaiting it, and the retry has to be synchronous.
 */
const failed = new Set<string>();

/**
 * Non-hook module → `createClientLogger` (policy §1). The anonymous reading
 * path never touches Convex, so a CDN corpus fetch failing is the one infra
 * signal worth an event (`infra.corpus.fetch`, phase-9 L6). Only failures are
 * logged (policy §9) — every successful chapter open is already a
 * `reading.open` event.
 */
const log = createClientLogger();

/**
 * Why a corpus fetch was rejected, carried on `CorpusFetchError` so the
 * `catch` in `cached()` can put a `reason` on `infra.corpus.fetch` without
 * re-deriving it from the error's shape. Each reason has a different operator
 * and a different fix, which is why none of them share a value.
 *
 * - `"network"` — no answer at all: `fetch()` threw (offline, DNS, TLS, CORS),
 *   or the body stream broke after the headers. A connectivity or
 *   configuration problem.
 * - `"status"` — the origin answered, with a 4xx/5xx (`status` rides along on
 *   the event). A deploy problem: a 404 is a corpus file the deploy did not
 *   ship, a 5xx is the origin failing.
 * - `"content_type"` — the response was a plain 200 but not JSON. This is
 *   wrangler's `not_found_handling: "single-page-application"` answering a
 *   missing or stale corpus path with the app shell's `index.html` instead of
 *   a 404: `res.ok` alone can't see it, and without this guard `res.json()`
 *   throws a `SyntaxError` that reads exactly like a generic parse failure.
 * - `"payload"` — a JSON response whose body did not parse, or parsed but
 *   failed the corpus schema. A build problem: the artifact itself is wrong.
 */
type CorpusFetchReason = "network" | "status" | "content_type" | "payload";

class CorpusFetchError extends Error {
  readonly reason: CorpusFetchReason;
  readonly status: number | undefined;
  constructor(
    reason: CorpusFetchReason,
    message: string,
    options: { cause?: unknown; status?: number } = {},
  ) {
    super(message, { cause: options.cause });
    this.name = "CorpusFetchError";
    this.reason = reason;
    this.status = options.status;
  }
}

/**
 * Tag a thrown error with its reason, keeping its message: the reading and
 * library error boundaries show `error.message` to the reader, and the
 * logged `error.*` is the cause's (see `cached()`), so neither changes.
 */
function failedWith(reason: CorpusFetchReason, err: unknown): CorpusFetchError {
  const message = err instanceof Error ? err.message : String(err);
  return new CorpusFetchError(reason, message, { cause: err });
}

async function fetchValidated<T>(
  url: string,
  schema: z.ZodType<T>,
): Promise<T> {
  let res: Response;
  try {
    res = await fetch(url);
  } catch (err) {
    throw failedWith("network", err);
  }
  if (!res.ok) {
    throw new CorpusFetchError(
      "status",
      `corpus fetch failed: ${res.status} ${url}`,
      { status: res.status },
    );
  }
  // Guard the content-type before ever calling res.json(): the SPA fallback
  // answers a missing path with 200 + text/html, and `res.ok` has no way to
  // see that. Checked with `includes` rather than equality to tolerate a
  // trailing `; charset=utf-8`.
  const contentType = res.headers.get("content-type") ?? "";
  if (!contentType.toLowerCase().includes("application/json")) {
    throw new CorpusFetchError(
      "content_type",
      `corpus fetch returned "${contentType || "(no content-type)"}", expected application/json: ${url}`,
    );
  }
  let json: unknown;
  try {
    json = await res.json();
  } catch (err) {
    // A SyntaxError is the bytes; anything else is the body stream dying
    // mid-read, which is the network again.
    throw failedWith(err instanceof SyntaxError ? "payload" : "network", err);
  }
  // JSON.parse output is `unknown` — validate the untrusted CDN payload against
  // the schema rather than trusting its shape (testing-philosophy: the
  // external-data boundary is exactly where types stop protecting us).
  try {
    return schema.parse(json);
  } catch (err) {
    throw failedWith("payload", err);
  }
}

/** What failed underneath the reason: `fetch()`'s TypeError, a SyntaxError, a ZodError. */
function loggedError(err: unknown): unknown {
  return err instanceof CorpusFetchError && err.cause !== undefined
    ? err.cause
    : err;
}

function failureFields(err: unknown): {
  reason: CorpusFetchReason;
  status?: number;
} {
  if (!(err instanceof CorpusFetchError)) return { reason: "network" };
  return err.status === undefined
    ? { reason: err.reason }
    : { reason: err.reason, status: err.status };
}

function cached<T>(url: string, schema: z.ZodType<T>): Promise<T> {
  const existing = cache.get(url);
  if (existing) return existing as Promise<T>;
  const promise = fetchValidated(url, schema).catch((err: unknown) => {
    // The REJECTED promise stays cached, and that is the whole point.
    //
    // Deleting it here — so a transient failure would be retried on the next
    // render — made a failed fetch UNBOUNDED. `use()` re-renders the component
    // the moment the promise it suspended on settles; with the entry gone, the
    // retry render called back in here, got a brand-new PENDING promise, and
    // suspended again instead of ever throwing. Nothing terminated that: no
    // backoff, no ceiling, no error state. A CDN hiccup (or, in dev, a missing
    // `public/corpus/` answering with the SPA's index.html) turned every open
    // tab into thousands of requests a second, one `infra.corpus.fetch` event
    // each, plus React's "suspended by an uncached promise" error for the
    // promise identity that changed under it every pass.
    //
    // Held, the retry render gets back the SAME settled promise, `use()`
    // rethrows its reason, and the nearest ErrorBoundary shows the reader what
    // went wrong. Retrying is then their decision, not a spin loop's —
    // `retryCorpusFetch` is what a "Try again" calls.
    failed.add(url);
    // Log the resource by its (content-hashed, public) basename — never the
    // full URL, which could carry a CDN origin/query (policy §11). Once per
    // failed URL per session now, rather than once per attempt.
    log.error("infra.corpus.fetch", loggedError(err), {
      resource: url.split("/").pop(),
      ...failureFields(err),
    });
    throw err;
  });
  cache.set(url, promise);
  return promise;
}

/**
 * Forget the FAILED fetches so the next render issues them again — the reader's
 * explicit "Try again" behind an ErrorBoundary, and the only way back from a
 * cached rejection.
 *
 * Successful entries are deliberately kept: their names are content-hashed, so
 * refetching could only return the same bytes under a new object identity,
 * which would churn every memo keyed on it (`use-corpus.ts`'s `ChapterData`
 * caches, and the sync hooks downstream of them).
 */
export function retryCorpusFetch(): void {
  for (const url of failed) cache.delete(url);
  failed.clear();
}

/** Fetch + validate `catalog.json`. */
export function fetchCatalog(): Promise<CorpusCatalog> {
  return cached(CATALOG_URL, corpusCatalogSchema);
}

/** Fetch + validate one `DocumentModel` by its catalog-declared filename. */
export function fetchDocumentModel(fileName: string): Promise<DocumentModel> {
  return cached(`${CORPUS_BASE}/${fileName}`, documentModelSchema);
}

/** Fetch + validate a chapter's anchor sidecar by its catalog filename. */
export function fetchAnchors(fileName: string): Promise<CorpusAnchors> {
  return cached(`${CORPUS_BASE}/${fileName}`, corpusAnchorsSchema);
}

// ----------------------------------------------------------------- selectors

/** Locate a book by slug in an already-loaded catalog. */
export function findBook(
  catalog: CorpusCatalog,
  bookSlug: string,
): CatalogBook | undefined {
  return catalog.books.find((b) => b.slug === bookSlug);
}

/** Locate a chapter within a book by its pairing number. */
export function findChapter(
  book: CatalogBook,
  chapterNumber: number,
): CatalogChapter | undefined {
  return book.chapters.find((c) => c.number === chapterNumber);
}

/** Locate a front/back matter document within a book by slug (PD-2). */
export function findMatter(
  book: CatalogBook,
  matterSlug: string,
): CatalogMatter | undefined {
  return book.matter.find((m) => m.slug === matterSlug);
}
